import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight, 
  MoreHorizontal,
  Eye,
  Trash,
  Download,
  FileText,
  Music,
  Video,
  Image as ImageIcon,
  AlertTriangle
} from 'lucide-react';
import AdminApi from '../../apis/adminApis';
import MediaViewModal from './modals/MediaViewModal';
import MediaDeleteConfirmModal from './modals/MediaDeleteConfirmModal';

interface Media {
  id: number;
  file_name: string;
  file_generated_name: string;
  file_type: string;
  file_size: number;
  path: string;
  description?: string;
  is_legacy: number;
  created_at: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  temporaryUrl?: string;
}

interface PaginationInfo {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

const MediaManagement: React.FC = () => {
  // State
  const [mediaList, setMediaList] = useState<Media[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0
  });
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<string>('desc');
  
  // Modals
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [menuOpenForMediaId, setMenuOpenForMediaId] = useState<number | null>(null);
  
  // Fetch media data
  const fetchMedia = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await AdminApi.getAllMedia({
        page,
        search: searchTerm,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        sort_by: sortBy,
        sort_direction: sortDirection,
        per_page: pagination.perPage
      });
      
      if (response.data.status === 'success') {
        const { data } = response.data;
        setMediaList(data.data);
        setPagination({
          currentPage: data.current_page,
          lastPage: data.last_page,
          perPage: data.per_page,
          total: data.total
        });
      } else {
        setError('Failed to fetch media. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching media. Please try again.');
      console.error('Error fetching media:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial data load
  useEffect(() => {
    fetchMedia(1);
  }, []);
  
  // Handle search and filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMedia(1); // Reset to page 1 when filters change
    }, 500); // Debounce for better performance
    
    return () => clearTimeout(timer);
  }, [searchTerm, typeFilter, sortBy, sortDirection]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    fetchMedia(page);
  };
  
  // Handle media deletion
  const handleDeleteMedia = async (mediaId: number) => {
    try {
      const response = await AdminApi.deleteMedia(mediaId);
      
      if (response.data.status === 'success') {
        // Remove media from the list
        setMediaList(mediaList.filter(media => media.id !== mediaId));
        setDeleteModalOpen(false);
        
        // If this was the last media on the page, go to previous page
        if (mediaList.length === 1 && pagination.currentPage > 1) {
          fetchMedia(pagination.currentPage - 1);
        } else {
          // Refresh current page
          fetchMedia(pagination.currentPage);
        }
      } else {
        setError('Failed to delete media. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting media. Please try again.');
      console.error('Error deleting media:', err);
    }
  };
  
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes?.toFixed(2) + ' MB';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' GB';
    else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' TB';
    else return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' TB';
  };

  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get media icon
  const getMediaIcon = (fileType: string) => {
    switch (fileType) {
      case 'image':
        return <ImageIcon className="h-6 w-6 text-blue-500" />;
      case 'video':
        return <Video className="h-6 w-6 text-purple-500" />;
      case 'audio':
        return <Music className="h-6 w-6 text-orange-500" />;
      case 'document':
      default:
        return <FileText className="h-6 w-6 text-green-500" />;
    }
  };

  // Handle click outside dropdown menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuOpenForMediaId !== null) {
        const target = event.target as HTMLElement;
        const dropdown = document.getElementById(`dropdown-${menuOpenForMediaId}`);
        
        if (dropdown && !dropdown.contains(target) && !target.closest('.dropdown-trigger')) {
          setMenuOpenForMediaId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenForMediaId]);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h2 className="text-xl font-semibold text-gray-800">Media Management</h2>
          <div className="mt-3 md:mt-0">
            <span className="text-sm text-gray-500 mr-2">Total Storage: </span>
            <span className="font-medium">
              {pagination.total > 0 
                ? formatFileSize(mediaList.reduce((total, media) => total + (media.file_size || 0), 0)) 
                : '0 MB'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by filename or user"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            
            <div className="relative">
              <div className="flex items-center justify-center">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg w-full appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="image">Images</option>
                  <option value="video">Videos</option>
                  <option value="audio">Audio</option>
                  <option value="document">Documents</option>
                </select>
                <Filter className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => fetchMedia(pagination.currentPage)}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-600">
          {error}
        </div>
      )}
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                File
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Size
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Uploaded
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : mediaList.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                  No media files found.
                </td>
              </tr>
            ) : (
              mediaList.map((media) => (
                <tr key={media.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded bg-gray-100 flex items-center justify-center">
                        {getMediaIcon(media.file_type)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {media.file_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {media.description || 'No description'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {media.user.first_name} {media.user.last_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {media.user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      media.file_type === 'image' ? 'bg-blue-100 text-blue-800' :
                      media.file_type === 'video' ? 'bg-purple-100 text-purple-800' :
                      media.file_type === 'audio' ? 'bg-orange-100 text-orange-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {media.file_type}
                    </span>
                    {media.is_legacy === 1 && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Legacy
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatFileSize(media.file_size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(media.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2 relative">
                      <button
                        className="text-gray-600 hover:text-gray-900"
                        title="View"
                        onClick={() => {
                          setSelectedMedia(media);
                          setViewModalOpen(true);
                        }}
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <a
                        href={media.temporaryUrl}
                        download={media.file_name}
                        className="text-blue-600 hover:text-blue-900"
                        title="Download"
                      >
                        <Download className="h-5 w-5" />
                      </a>
                      <button
                        className="text-gray-600 hover:text-gray-900 dropdown-trigger"
                        title="More actions"
                        onClick={() => setMenuOpenForMediaId(menuOpenForMediaId === media.id ? null : media.id)}
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </button>
                      
                      {/* Dropdown menu */}
                      {menuOpenForMediaId === media.id && (
                        <div 
                          id={`dropdown-${media.id}`}
                          className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200"
                        >
                          <button
                            className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                            onClick={() => {
                              setSelectedMedia(media);
                              setDeleteModalOpen(true);
                              setMenuOpenForMediaId(null);
                            }}
                          >
                            <Trash className="mr-2 h-4 w-4 text-red-500" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {!loading && mediaList.length > 0 && (
        <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                pagination.currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.lastPage}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                pagination.currentPage === pagination.lastPage
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.perPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.perPage, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                    pagination.currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {/* Pagination buttons */}
                {[...Array(pagination.lastPage)].map((_, i) => {
                  const page = i + 1;
                  // Show first page, last page, current page, and pages around current page
                  if (
                    page === 1 ||
                    page === pagination.lastPage ||
                    (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.currentPage === page
                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }
                  
                  // Show ellipsis
                  if (
                    (page === 2 && pagination.currentPage > 3) ||
                    (page === pagination.lastPage - 1 && pagination.currentPage < pagination.lastPage - 2)
                  ) {
                    return (
                      <span
                        key={page}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    );
                  }
                  
                  return null;
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.lastPage}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                    pagination.currentPage === pagination.lastPage
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {/* Modals */}
      {selectedMedia && (
        <>
          <MediaViewModal
            isOpen={viewModalOpen}
            onClose={() => setViewModalOpen(false)}
            media={selectedMedia}
          />
          
          <MediaDeleteConfirmModal
            isOpen={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={() => handleDeleteMedia(selectedMedia.id)}
            media={selectedMedia}
          />
        </>
      )}
    </div>
  );
};

export default MediaManagement;